import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { db } from "../firebase";
import {
  collection, onSnapshot, getDocs, addDoc, deleteDoc, doc,
  query, orderBy, serverTimestamp,
} from "firebase/firestore";

const RSVP_COL = "baptism_rsvps";
const LB_COL = "baptism_leaderboard";
const WINNERS_COL = "baptism_raffle_winners";

const G = "#c9a55a";
const NAVY = "#12122a";
const ADMIN_PASS = "admin123";

function mergeParticipants(rsvps, lbPlayers) {
  const map = new Map();
  rsvps
    .filter((r) => r.attending === "yes")
    .forEach((r) => {
      const key = r.name.trim().toLowerCase();
      if (!map.has(key)) map.set(key, { name: r.name.trim(), sources: [] });
      if (!map.get(key).sources.includes("rsvp")) map.get(key).sources.push("rsvp");
    });
  lbPlayers.forEach((p) => {
    const key = p.name.trim().toLowerCase();
    if (!map.has(key)) map.set(key, { name: p.name.trim(), sources: [] });
    if (!map.get(key).sources.includes("quiz")) map.get(key).sources.push("quiz");
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export default function BaptismRaffle() {
  const [participants, setParticipants] = useState([]);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [spinning, setSpinning] = useState(false);
  const [display, setDisplay] = useState("?");
  const [lastWinner, setLastWinner] = useState(null);
  const [excludeWinners, setExcludeWinners] = useState(true);
  const [showParticipants, setShowParticipants] = useState(false);

  const [adminOpen, setAdminOpen] = useState(false);
  const [adminPass, setAdminPass] = useState("");
  const [adminUnlocked, setAdminUnlocked] = useState(() => localStorage.getItem("baptism_admin_token") === "true");
  const [adminError, setAdminError] = useState(false);

  const spinTimer = useRef(null);
  const rsvpsRef = useRef([]);
  const lbRef = useRef([]);

  useEffect(() => {
    if (!document.getElementById("baptism-fonts")) {
      const link = document.createElement("link");
      link.id = "baptism-fonts";
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&display=swap";
      document.head.appendChild(link);
    }

    const loadSources = async () => {
      try {
        const [rsvpSnap, lbSnap] = await Promise.all([
          getDocs(collection(db, RSVP_COL)),
          getDocs(collection(db, LB_COL)),
        ]);
        const rsvps = rsvpSnap.docs.map((d) => d.data());
        const lb = lbSnap.docs.map((d) => d.data());
        rsvpsRef.current = rsvps;
        lbRef.current = lb;
        setParticipants(mergeParticipants(rsvps, lb));
      } catch (err) {
        console.error("Failed to load participants:", err);
      } finally {
        setLoading(false);
      }
    };
    loadSources();

    const unsub = onSnapshot(
      query(collection(db, WINNERS_COL), orderBy("drawnAt", "asc")),
      (snap) => setWinners(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return () => { unsub(); clearTimeout(spinTimer.current); };
  }, []);

  const getPool = () =>
    excludeWinners
      ? participants.filter((p) => !winners.some((w) => w.name.toLowerCase() === p.name.toLowerCase()))
      : participants;

  const startDraw = async () => {
    const pool = getPool();
    if (pool.length === 0 || spinning) return;

    const winner = pool[Math.floor(Math.random() * pool.length)];
    setSpinning(true);
    setLastWinner(null);

    let elapsed = 0;
    const totalMs = 3200;

    const tick = () => {
      const progress = elapsed / totalMs;
      const delay = Math.round(55 + progress * progress * 520);
      elapsed += delay;

      const isLast = elapsed + delay >= totalMs;

      spinTimer.current = setTimeout(() => {
        if (isLast) {
          setDisplay(winner.name);
          setSpinning(false);
          setLastWinner(winner);
          addDoc(collection(db, WINNERS_COL), {
            name: winner.name,
            sources: winner.sources,
            prize: `Prize #${winners.length + 1}`,
            drawnAt: serverTimestamp(),
          }).catch(console.error);
        } else {
          setDisplay(pool[Math.floor(Math.random() * pool.length)].name);
          tick();
        }
      }, delay);
    };

    tick();
  };

  const removeWinner = (id) => deleteDoc(doc(db, WINNERS_COL, id));

  const clearWinners = async () => {
    await Promise.all(winners.map((w) => deleteDoc(doc(db, WINNERS_COL, w.id))));
    setLastWinner(null);
    setDisplay("?");
  };

  const handleAdminUnlock = () => {
    if (adminPass === ADMIN_PASS) { setAdminUnlocked(true); setAdminError(false); }
    else setAdminError(true);
  };

  const pool = getPool();

  return (
    <div style={{ background: "linear-gradient(160deg,#0d0d1e,#12122a,#0a0a18)", minHeight: "100vh", fontFamily: "'Cormorant Garamond',Georgia,serif" }}>
      <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />

      <div className="max-w-2xl mx-auto px-4 py-12">

        {/* Header */}
        <div className="text-center mb-12">
          <p style={{ letterSpacing: "0.28em", color: `${G}90` }} className="text-xs uppercase mb-3">
            Baptism of Munachimso Zoe Maxwell
          </p>
          <div className="text-5xl mb-3">🎟️</div>
          <h1 style={{ fontFamily: "'Playfair Display',serif", color: G, lineHeight: 1.1 }} className="text-5xl sm:text-6xl font-bold mb-3">
            Raffle Draw
          </h1>
          <p style={{ color: "#6b6b8a" }} className="text-sm italic mb-8">
            {loading ? "Loading participants…" : `${participants.length} entrants · ${pool.length} in pool`}
          </p>
          <div className="flex items-center justify-center gap-3 max-w-xs mx-auto">
            <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,transparent,${G}40)` }} />
            <span style={{ color: G }}>✦</span>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,${G}40,transparent)` }} />
          </div>
        </div>

        {/* Slot display */}
        <div className="flex justify-center mb-8">
          <div
            style={{
              background: "rgba(255,255,255,0.04)",
              border: `2px solid ${spinning ? G : lastWinner ? "#f59e0b" : `${G}40`}`,
              boxShadow: spinning ? `0 0 32px ${G}40` : lastWinner ? `0 0 48px #f59e0b30` : "none",
              minWidth: 320,
              transition: "border-color 0.3s, box-shadow 0.3s",
            }}
            className="rounded-3xl px-8 py-10 text-center"
          >
            {lastWinner && !spinning && (
              <p style={{ color: `${G}80`, letterSpacing: "0.2em" }} className="text-xs uppercase mb-3">
                🎉 Winner!
              </p>
            )}
            <p
              style={{
                fontFamily: "'Playfair Display',serif",
                color: spinning ? G : lastWinner ? "#f1f5f9" : `${G}50`,
                fontSize: display === "?" ? "3rem" : display.length > 20 ? "1.6rem" : display.length > 12 ? "2rem" : "2.5rem",
                lineHeight: 1.2,
                minHeight: "3.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: spinning ? "none" : "color 0.5s ease",
              }}
              className={spinning ? "animate-pulse" : lastWinner ? "animate-bounce" : ""}
            >
              {display}
            </p>
            {lastWinner && !spinning && (
              <div className="mt-3 flex justify-center gap-2 flex-wrap">
                {lastWinner.sources.map((s) => (
                  <span key={s} style={{ background: `${G}20`, color: G, letterSpacing: "0.08em" }} className="text-xs px-2.5 py-1 rounded-full uppercase">
                    {s === "rsvp" ? "✉ RSVP" : "🎯 Quiz"}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Draw button — admin only */}
        {adminUnlocked && (
          <div className="flex flex-col items-center gap-4 mb-10">
            <button
              onClick={startDraw}
              disabled={spinning || pool.length === 0}
              style={{
                background: spinning ? `${G}40` : `linear-gradient(135deg,#c9a55a,#a07838)`,
                fontFamily: "'Cormorant Garamond',serif",
                letterSpacing: "0.2em",
              }}
              className="text-white font-semibold py-4 px-12 rounded-2xl uppercase text-sm disabled:opacity-50 hover:opacity-90 active:opacity-75 transition-opacity shadow-xl text-lg"
            >
              {spinning ? "Drawing…" : pool.length === 0 ? "No Eligible Entrants" : "🎟 Draw Winner"}
            </button>

            <label style={{ color: "#6b6b8a" }} className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={excludeWinners}
                onChange={(e) => setExcludeWinners(e.target.checked)}
                style={{ accentColor: G, width: 15, height: 15 }}
              />
              Exclude previous winners from pool
            </label>
          </div>
        )}

        {/* Winners list */}
        {winners.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h2 style={{ fontFamily: "'Playfair Display',serif", color: G }} className="text-xl font-semibold">
                🏆 Winners
              </h2>
              {adminUnlocked && (
                <button onClick={clearWinners} style={{ color: "#f87171" }} className="text-xs hover:opacity-70 transition-opacity">
                  Clear all
                </button>
              )}
            </div>
            <div className="space-y-3">
              {[...winners].reverse().map((w, i) => (
                <div
                  key={w.id}
                  style={{
                    background: i === 0 ? `${G}12` : "rgba(255,255,255,0.04)",
                    border: `1px solid ${i === 0 ? G : `${G}30`}`,
                  }}
                  className="rounded-2xl px-5 py-4 flex items-center gap-4"
                >
                  <span style={{ fontFamily: "'Playfair Display',serif", color: G, fontSize: "1.3rem", fontWeight: 700, width: 28, flexShrink: 0 }}>
                    {winners.length - i}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p style={{ color: "#f1f5f9", fontFamily: "'Playfair Display',serif" }} className="font-semibold truncate">
                      {w.name}
                    </p>
                    <div className="flex gap-1.5 mt-1 flex-wrap">
                      {w.sources?.map((s) => (
                        <span key={s} style={{ color: `${G}70`, letterSpacing: "0.08em" }} className="text-xs uppercase">
                          {s === "rsvp" ? "✉ RSVP" : "🎯 Quiz"}
                        </span>
                      ))}
                    </div>
                  </div>
                  <span style={{ color: `${G}60` }} className="text-xs shrink-0">{w.prize}</span>
                  {adminUnlocked && (
                    <button onClick={() => removeWinner(w.id)} style={{ color: "#6b6b8a" }} className="text-lg leading-none hover:text-red-400 transition-colors ml-1">
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Participants list toggle */}
        <div className="mb-10">
          <button
            onClick={() => setShowParticipants(!showParticipants)}
            style={{ color: `${G}70`, border: `1px solid ${G}30` }}
            className="w-full text-sm py-2.5 rounded-xl hover:opacity-80 transition-opacity"
          >
            {showParticipants ? "Hide" : "Show"} all entrants ({participants.length})
          </button>
          {showParticipants && (
            <div style={{ border: `1px solid ${G}20` }} className="mt-3 rounded-2xl overflow-hidden">
              {participants.length === 0 ? (
                <p style={{ color: "#6b6b8a" }} className="text-center text-sm py-8">No participants yet.</p>
              ) : (
                <div className="divide-y divide-white/5 max-h-80 overflow-y-auto">
                  {participants.map((p) => {
                    const isWinner = winners.some((w) => w.name.toLowerCase() === p.name.toLowerCase());
                    return (
                      <div key={p.name} className="flex items-center gap-3 px-4 py-2.5">
                        <div className="flex gap-1 shrink-0">
                          {p.sources.map((s) => (
                            <span key={s} style={{ background: `${G}15`, color: `${G}80` }} className="text-xs px-1.5 py-0.5 rounded uppercase">
                              {s === "rsvp" ? "R" : "Q"}
                            </span>
                          ))}
                        </div>
                        <p style={{ color: isWinner ? "#6b6b8a" : "#e2e8f0", textDecoration: isWinner ? "line-through" : "none" }} className="text-sm flex-1">
                          {p.name}
                        </p>
                        {isWinner && <span style={{ color: G }} className="text-xs">🏆</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Nav links */}
        <div className="flex justify-center gap-6 mb-12 flex-wrap">
          <Link to="/baptism/leaderboard" style={{ color: `${G}70` }} className="text-sm hover:opacity-80 transition-opacity">🏆 Leaderboard</Link>
          <Link to="/baptism/quiz" style={{ color: `${G}70` }} className="text-sm hover:opacity-80 transition-opacity">🎯 Quizzes</Link>
          <Link to="/baptism" style={{ color: `${G}70` }} className="text-sm hover:opacity-80 transition-opacity">✦ Invite</Link>
        </div>

        {!adminUnlocked && (
          <div className="text-center pb-4">
            <Link to="/baptism/admin" style={{ color: `${G}50` }} className="text-xs hover:opacity-70 transition-opacity">Admin →</Link>
          </div>
        )}
      </div>

      <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />
    </div>
  );
}
